import type { AdminReportMediaDto } from '@carcheck/shared';
import Box from '@mui/material/Box';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { MediaTile } from './MediaTile';
import { labelReportMedia } from '../_lib/mediaLabels';

/**
 * 4-column responsive media grid for the report detail drawer (AUT-127).
 * Mirrors the mockup `.media-grid` (grid-template-columns: repeat(4,
 * 1fr)) with a 10px gap. On the smallest sm-and-below the grid drops
 * to 2-col so the drawer stays usable on tablet-portrait widths.
 *
 * Per-tile label is the i18n string "фото N" / "видео N" / "PDF N" with a
 * type-local running index so the operator can refer to a specific
 * shot ("в фото 6 нет VIN") without depending on the category. The
 * numbering comes from the shared `labelReportMedia` so the Lightbox
 * shows the SAME label as the tile that opened it.
 */
export interface MediaGalleryProps {
  media: AdminReportMediaDto[];
  /** Receives the clicked media id — the caller maps it into whatever list it pages over. */
  onTileClick: (mediaId: string) => void;
  onTileError?: () => void;
}

export const MediaGallery = ({
  media,
  onTileClick,
  onTileError,
}: MediaGalleryProps): ReactElement => {
  const { t } = useTranslation();

  // Pre-computed in one pass so the render `.map` stays pure (no `++`
  // side-effect across iterations).
  const labeled = labelReportMedia(media, t);

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(2, 1fr)',
          sm: 'repeat(4, 1fr)',
        },
        gap: 1.25,
      }}
    >
      {labeled.map(({ item, label }) => (
        <MediaTile
          key={item.id}
          media={item}
          label={label}
          onClick={() => onTileClick(item.id)}
          {...(onTileError ? { onLoadError: onTileError } : {})}
        />
      ))}
    </Box>
  );
};
